/**
 * HH Finance - Main Dashboard Component
 *
 * Full financial dashboard using equa-patternlib components
 */

import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Alert } from '@shared/components/alert';
import { Button } from '@shared/components/button';
import { useFinanceDashboard } from '../hooks';
import { hhFinanceApi } from '../services';
import { formatRelativeTime } from '../utils';
import { MetricCard } from './MetricCard';
import { AgingSummary } from './AgingSummary';
import { BankAccountsTable } from './BankAccountsTable';
import { TransactionsTable } from './TransactionsTable';
import { SchedulerStatus } from './SchedulerStatus';

const DashboardContainer = styled.div`
  padding: 24px;
  background-color: ${({ theme }) => theme.backgroundPrimary || '#f5f5f5'};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  margin: 4px 0 0 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  margin-bottom: 24px;
`;

export interface FinanceDashboardProps {
  onNavigate?: (path: string) => void;
}

export const FinanceDashboard: React.FC<FinanceDashboardProps> = ({
  onNavigate,
}) => {
  const { data, loading, error, refetch } = useFinanceDashboard();

  const handleRunJob = useCallback(async (jobId: string) => {
    try {
      await hhFinanceApi.runJob(jobId);
      // Optionally show success notification
      refetch();
    } catch (err) {
      console.error('Failed to run job:', err);
      // Optionally show error notification
    }
  }, [refetch]);

  const handleToggleJob = useCallback(async (jobId: string, enabled: boolean) => {
    try {
      await hhFinanceApi.toggleJob(jobId, enabled);
      refetch();
    } catch (err) {
      console.error('Failed to toggle job:', err);
    }
  }, [refetch]);

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>Loading financial data...</LoadingContainer>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <Header>
          <TitleSection>
            <Title>💰 Hills & Hollows Finance</Title>
          </TitleSection>
        </Header>
        <ErrorContainer>
          <Alert
            variant="error"
            title="Connection Error"
            dismissible={false}
          >
            {error}
            <br /><br />
            Make sure the HH Finance Agent API is running on port 19792.
            <br /><br />
            <Button variant="solid" onClick={refetch}>
              Retry
            </Button>
          </Alert>
        </ErrorContainer>
      </DashboardContainer>
    );
  }

  if (!data?.summary) {
    return (
      <DashboardContainer>
        <LoadingContainer>No data available</LoadingContainer>
      </DashboardContainer>
    );
  }

  const { summary, scheduler, jobs } = data;
  const { financials, bankAccounts, receivables, payables } = summary;

  return (
    <DashboardContainer>
      {/* Header */}
      <Header>
        <TitleSection>
          <Title>
            💰 Hills & Hollows Finance
          </Title>
          <Subtitle>
            Last updated: {formatRelativeTime(summary.generatedAt)}
          </Subtitle>
        </TitleSection>
        <Button variant="solid" onClick={refetch}>
          Refresh
        </Button>
      </Header>

      {/* Key Metrics */}
      <MetricsGrid>
        <MetricCard
          label="Total Assets"
          value={financials.totalAssets}
          format="currency"
          trend="up"
          icon="📈"
        />
        <MetricCard
          label="Total Liabilities"
          value={financials.totalLiabilities}
          format="currency"
          trend="down"
          icon="📉"
        />
        <MetricCard
          label="Total Equity"
          value={financials.totalEquity}
          format="currency"
          icon="💎"
        />
        <MetricCard
          label="Accounts Receivable"
          value={receivables.aging.total}
          format="currency"
          trend="up"
          icon="📥"
        />
        <MetricCard
          label="Accounts Payable"
          value={payables.aging.total}
          format="currency"
          trend="neutral"
          icon="📤"
        />
      </MetricsGrid>

      {/* Bank Accounts */}
      <Section>
        <BankAccountsTable accounts={bankAccounts} />
      </Section>

      {/* AR/AP Aging */}
      <TwoColumnGrid>
        <AgingSummary
          title="Accounts Receivable Aging"
          icon="📥"
          aging={receivables.aging}
          type="receivable"
        />
        <AgingSummary
          title="Accounts Payable Aging"
          icon="📤"
          aging={payables.aging}
          type="payable"
        />
      </TwoColumnGrid>

      {/* Open Invoices / Bills */}
      <TwoColumnGrid>
        <TransactionsTable
          title="Open Invoices"
          icon="📄"
          type="invoices"
          transactions={receivables.openInvoices}
          limit={5}
          onViewAll={() => onNavigate?.('/hh-finance/invoices')}
        />
        <TransactionsTable
          title="Open Bills"
          icon="📋"
          type="bills"
          transactions={payables.openBills}
          limit={5}
          onViewAll={() => onNavigate?.('/hh-finance/bills')}
        />
      </TwoColumnGrid>

      {/* Scheduler */}
      {scheduler && jobs.length > 0 && (
        <Section>
          <SchedulerStatus
            status={scheduler}
            jobs={jobs}
            onRunJob={handleRunJob}
            onToggleJob={handleToggleJob}
          />
        </Section>
      )}
    </DashboardContainer>
  );
};

export default FinanceDashboard;
